import React, { useCallback, useEffect, useRef, useState } from "react";
import { History, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import { MonthInput } from "@/components/ui/month-input";
import {
  cajaBancosAPI,
  opcionesAPI,
  type CierreDiario,
  type MovimientoCaja,
  type Sucursal,
} from "@/lib/api";
import { toast } from "sonner";
import { CierreDiarioListado } from "./CierreDiarioListado";
import { MovimientosListado } from "./MovimientosListado";
import {
  applyHistoricoFecha,
  applyHistoricoMes,
  applyHistoricoRango,
  clampHistoricoDateFilter,
  defaultHistoricoDateFilter,
  historicoFetchParams,
  historicoHastaMax,
  localYmd,
  previousMonthYm,
  type HistoricoDateFilter,
} from "./cajaHistorico";

interface HistoricoSectionProps {
  canMovimientos: boolean;
  canCierre: boolean;
  isActive?: boolean;
}

export function HistoricoSection({
  canMovimientos,
  canCierre,
  isActive = true,
}: HistoricoSectionProps) {
  const [filter, setFilter] = useState<HistoricoDateFilter>(() =>
    defaultHistoricoDateFilter(localYmd()),
  );
  const [hasSearched, setHasSearched] = useState(false);
  const [movimientos, setMovimientos] = useState<MovimientoCaja[]>([]);
  const [cierres, setCierres] = useState<CierreDiario[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  useEffect(() => {
    let cancelled = false;
    opcionesAPI.listSucursales().then((rows) => {
      if (!cancelled) setSucursales(rows.filter((s) => s.activo !== false));
    }).catch(() => {
      if (!cancelled) setSucursales([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const todayYmd = localYmd();
  const maxYmd = historicoHastaMax(todayYmd);
  const maxYm = previousMonthYm(todayYmd);

  const patchFilter = (
    updater: (prev: HistoricoDateFilter) => HistoricoDateFilter,
  ) => setFilter((prev) => clampHistoricoDateFilter(updater(prev), localYmd()));

  const runSearch = useCallback(() => {
    if (!isActive) return;
    const params = historicoFetchParams(true, filterRef.current, localYmd());
    if (!params) return;
    const requestId = ++requestIdRef.current;
    setHasSearched(true);
    setLoading(true);
    Promise.all([
      canMovimientos ? cajaBancosAPI.listMovimientos(params) : Promise.resolve([] as MovimientoCaja[]),
      canCierre ? cajaBancosAPI.listCierres(params) : Promise.resolve([] as CierreDiario[]),
    ])
      .then(([movs, cierresData]) => {
        if (requestId !== requestIdRef.current) return;
        setMovimientos(movs);
        setCierres(cierresData);
      })
      .catch((error) => {
        console.error("Error fetching Histórico de Caja:", error);
        if (requestId === requestIdRef.current) {
          toast.error("Error al cargar el histórico de Caja y Bancos");
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [canCierre, canMovimientos, isActive]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-md">
          <History className="h-5 w-5 text-white" />
        </div>
        <h3 className="text-2xl font-bold">Histórico</h3>
      </div>

      <form
        className="flex flex-wrap gap-4 items-end"
        onSubmit={(event) => {
          event.preventDefault();
          runSearch();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="historico-fecha">Fecha</Label>
          <DateInput
            id="historico-fecha"
            value={filter.fecha}
            onChange={(ymd) => patchFilter((prev) => applyHistoricoFecha(prev, ymd))}
            max={maxYmd}
            className="w-[200px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="historico-desde">Desde</Label>
          <DateInput
            id="historico-desde"
            value={filter.desde}
            onChange={(ymd) => patchFilter((prev) => applyHistoricoRango(ymd, prev.hasta))}
            max={maxYmd}
            className="w-[200px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="historico-hasta">Hasta</Label>
          <DateInput
            id="historico-hasta"
            value={filter.hasta}
            onChange={(ymd) => patchFilter((prev) => applyHistoricoRango(prev.desde, ymd))}
            max={maxYmd}
            className="w-[200px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="historico-mes">Mes</Label>
          <MonthInput
            id="historico-mes"
            value={filter.mes}
            max={maxYm}
            onChange={(mes) => patchFilter(() => applyHistoricoMes(mes))}
          />
        </div>
        <Button type="submit" size="sm" className="flex items-center gap-1">
          <Search className="h-4 w-4" />
          Buscar
        </Button>
      </form>

      {!hasSearched ? (
        <p className="text-sm text-muted-foreground">Usá Buscar.</p>
      ) : loading && movimientos.length === 0 && cierres.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-8">
          {canMovimientos && (
            <div className="space-y-3">
              <h4 className="text-lg font-semibold">Movimientos</h4>
              <MovimientosListado movimientos={movimientos} isReadOnly />
            </div>
          )}
          {canCierre && (
            <div className="space-y-3">
              <h4 className="text-lg font-semibold">Cierres diarios</h4>
              <CierreDiarioListado
                cierres={cierres}
                sucursales={sucursales}
                isReadOnly
                hideDateFilter
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
